/**
 * A component for the SmartDoc pill only.
 *
 * This is used to render the SmartDoc component since SmartDoc is different from other smart pills
 * because it depends on an async service call to get all suggestions, so unlike other static pills,
 * SmartDoc needs to support a dynamic scenario, which is against the existing SmartPill pattern and
 * will need an separate render logic.
 *
 * If we found more and more smart pills that required the dynamic update in the future, we should
 * consider refactor the whole SmartPill component render logic.
 */

import * as React from 'react';
import AttachmentSuggestionButton from '../components/AttachmentSuggestionButton';
import createMessageWithAttachmentAction from '../actions/createMessageWithAttachmentAction';
import extractAttachmentSuggestions from '../utils/extractAttachmentSuggestions';
import loc from 'owa-localize';
import RequestStatus from '../store/schema/RequestStatus';
import SmartDocStatusAndSuggestionStore from '../store/schema/SmartDocStatusAndSuggestionStore';
import { assertNever } from 'owa-assert';
import { ClientItem } from 'owa-mail-store';
import { ReplyWithAttachmentSuggestion, ReplyWithEndpointType } from 'owa-attachment-suggestions';
import { ControlIcons } from 'owa-control-icons';
import { DefaultButton, IButtonStyles } from '@fluentui/react/lib/Button';
import { IIconProps } from '@fluentui/react/lib/Icon';
import { observer } from 'mobx-react-lite';
import {
    smartDocButtonText,
    smartDocMenuHeaderText,
    smartDocTooltip,
} from '../utils/smartDocButton.locstring.json';
import { isFeatureEnabled } from 'owa-feature-flags';
import { SmartDocOption } from 'owa-mail-smart-pill-features';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import {
    computerMenuText,
    cloudLocationsMenuText,
    uploadAndShareFromCloudMenuText,
    uploadAndShareFromCloudMenuTooltip,
} from 'owa-attachment-filepicker/lib/index.locstring.json';
import {
    SmartPillLogData,
    logOnClickDatapoint,
    SMART_DOC_MENU_CLICKED,
    SMART_DOC_MENU_CLOSED,
    SMART_DOC_SPINNER_DISPOSED,
    SMART_PILL_CLICKED,
} from 'owa-smart-pill';
import {
    IContextualMenuItem,
    IContextualMenuProps,
    ContextualMenuItemType,
} from '@fluentui/react/lib/ContextualMenu';
import { PerformanceDatapoint } from 'owa-analytics';

const styles = require('../components/SmartDocPill.scss');
const classNames = require('classnames/bind').bind(styles);

export interface SmartDocPillProps {
    store: SmartDocStatusAndSuggestionStore;
    item: ClientItem;
    logData: SmartPillLogData;
    position: number;
    menuItemStyle: IButtonStyles;
    pillsToShowTypes: string[];
}

export default observer(function SmartDocPill(props: SmartDocPillProps) {
    const { store, item, logData, pillsToShowTypes } = props;

    const isMenuOpened = React.useRef<boolean>(false);
    const performanceDatapoint = React.useRef<PerformanceDatapoint>();

    // Construct the SmartDocPill dropdown menu.
    const menuItems: IContextualMenuItem[] = [];
    const suggestionItems = [];
    menuItems.push(createContextualMenuItemHeader());

    // Check the store's status to find out whether the SmartDoc suggestion is available or not,
    // update the menu bar with the status and result accordingly.
    switch (store.status) {
        case RequestStatus.NotInitiated:
        case RequestStatus.Complete:
            if (store.suggestions?.length) {
                const suggestions: ReplyWithAttachmentSuggestion[] = extractAttachmentSuggestions(
                    store.suggestions
                );
                suggestions.forEach((suggestion, index) => {
                    menuItems.push(
                        createContextualMenuItemAttachmentButton(suggestion, index, item, logData)
                    );
                    suggestionItems.push(extractSuggestionInfoForLogging(suggestion));
                });
            }
            menuItems.push(createContextualMenuItemSeparator());
            break;
        case RequestStatus.Pending:
            // If the request is still processing, display a spinner instead.
            menuItems.push(createContextualMenuItemSpinner());
            menuItems.push(createContextualMenuItemSeparator());
            break;
        case RequestStatus.Error:
            // If the request failed to pull back the suggestions, we should drop the
            // suggest section.
            menuItems.pop();
            break;
        default:
            assertNever(store.status);
    }

    menuItems.push(
        createContextualMenuItemDefaultButton(
            item,
            logData,
            loc(computerMenuText), // text
            loc(computerMenuText), // ariaLabel
            {
                iconName: ControlIcons.System,
                styles: {
                    root: classNames(styles.iconRoot, styles.iconRootComputer),
                },
            } as IIconProps,
            SmartDocOption.LocalFiles
        )
    );
    menuItems.push(
        createContextualMenuItemDefaultButton(
            item,
            logData,
            loc(cloudLocationsMenuText), // text
            loc(cloudLocationsMenuText), // ariaLabel
            {
                iconName: ControlIcons.Cloud,
                styles: {
                    root: classNames(styles.iconRoot, styles.iconRootCloud),
                },
            } as IIconProps,
            SmartDocOption.CloudFiles
        )
    );
    menuItems.push(
        createContextualMenuItemDefaultButton(
            item,
            logData,
            loc(uploadAndShareFromCloudMenuText), // text
            loc(uploadAndShareFromCloudMenuTooltip), // ariaLabel
            {
                iconName: ControlIcons.CloudUpload,
                styles: {
                    root: classNames(styles.iconRoot, styles.iconRootUploadAndShare),
                },
            },
            SmartDocOption.UploadAndShare
        )
    );

    const logPerformanceDatapoint = (dp: PerformanceDatapoint, isSuccessful: boolean) => {
        if (dp) {
            const timeClicked: string = new Date().toISOString();
            const pillObj = {
                data: [
                    {
                        ...logData.mainPayload,
                        timeClicked: timeClicked,
                        numOfFileSuggestions: suggestionItems.length,
                        isSuccessful: isSuccessful,
                    },
                ],
            };
            dp.addCustomData([JSON.stringify(pillObj)]);
            dp.addCosmosOnlyData(logData.cosmosOnlyData);
            dp.end();
        }
    };

    React.useEffect(() => {
        // Log the performance datapoint when the spinner is disposed by server status change.
        if (
            isMenuOpened.current &&
            (store.status === RequestStatus.Complete || store.status === RequestStatus.Error)
        ) {
            logPerformanceDatapoint(
                performanceDatapoint.current,
                store.status === RequestStatus.Complete /* isSuccessful */
            );
        }
    });

    const menuProps: IContextualMenuProps = {
        items: menuItems,
        onMenuOpened: () => {
            isMenuOpened.current = true;
            logOnClickDatapoint(SMART_PILL_CLICKED, logData, {
                numOfFileSuggestions: suggestionItems.length,
                numOfPillsShown: pillsToShowTypes.length,
                typesOfPillsShown: pillsToShowTypes,
            });

            // When the menu opens, start a performance datapoint that captures the persistence of the spinner.
            if (store.status === RequestStatus.Pending) {
                performanceDatapoint.current = new PerformanceDatapoint(
                    SMART_DOC_SPINNER_DISPOSED,
                    {
                        isCore: true,
                        isCosmosDatapoint: true,
                    }
                );
            }
        },
        onMenuDismissed: () => {
            isMenuOpened.current = false;
            logOnClickDatapoint(SMART_DOC_MENU_CLOSED, logData, {
                numOfFileSuggestions: suggestionItems.length,
                suggestions: suggestionItems,
            });

            // Log the performance datapoint when the spinner is disposed by menu closure.
            if (store.status === RequestStatus.Pending) {
                logPerformanceDatapoint(performanceDatapoint.current, false /* isSuccessful */);
            }

            // When the menu closes, remove the performance datapoint.
            performanceDatapoint.current = null;
        },
    };

    const buttonText = loc(smartDocButtonText);

    return (
        <DefaultButton
            ariaDescription={buttonText}
            title={isFeatureEnabled('mc-smartPillsTooltipV2') ? loc(smartDocTooltip) : buttonText}
            iconProps={{ iconName: ControlIcons.Attach }}
            key={props.position} // use position as key, since it's guaranteed to be unique per smart pill
            split={false}
            styles={props.menuItemStyle}
            text={buttonText}
            menuProps={menuProps}
        />
    );
});

function createContextualMenuItemHeader(): IContextualMenuItem {
    return {
        key: 'replyWithHeader',
        name: loc(smartDocMenuHeaderText),
        title: loc(smartDocMenuHeaderText),
        itemType: ContextualMenuItemType.Header,
        iconProps: {
            iconName: null,
            styles: {
                root: {
                    margin: '0px',
                    width: '0px',
                },
            },
        },
        className: styles.replyWithHeaderForBottomBar,
    };
}

function createContextualMenuItemAttachmentButton(
    suggestion: ReplyWithAttachmentSuggestion,
    index: number,
    clientItem: ClientItem,
    logData: SmartPillLogData
): IContextualMenuItem {
    return {
        key: 'fileSuggestion' + index,
        className: styles.attachmentSuggestionButtonRoot,
        onRender: (
            item: any,
            dismissMenu: (ev?: any, dismissAll?: boolean) => void
        ): React.ReactNode => {
            return (
                <AttachmentSuggestionButton
                    item={clientItem}
                    suggestion={suggestion}
                    smartPillLogData={logData}
                    onAttachmentClicked={createMessageWithAttachmentAction}
                    dismissMenu={dismissMenu}
                />
            );
        },
    };
}

function createContextualMenuItemSpinner(): IContextualMenuItem {
    return {
        key: 'spinner',
        className: styles.attachmentSuggestionButtonRoot,
        onRender: (): React.ReactNode => {
            return <Spinner size={SpinnerSize.medium} className={styles.loadingSpinner} />;
        },
    };
}

function createContextualMenuItemSeparator(): IContextualMenuItem {
    return {
        key: 'replyWithSeparator',
        iconProps: {
            iconName: null,
            styles: {
                root: {
                    margin: '0px',
                    width: '0px',
                },
            },
        },
        className: styles.replyWithSeparator,
        itemType: ContextualMenuItemType.Divider,
    };
}

function createContextualMenuItemDefaultButton(
    clientItem: ClientItem,
    logData: SmartPillLogData,
    name: string,
    ariaLabel: string,
    iconProps: IIconProps,
    option: SmartDocOption
): IContextualMenuItem {
    const optionString: string = SmartDocOption[option];
    return {
        key: optionString,
        name: name,
        ariaLabel: ariaLabel,
        title: ariaLabel,
        iconProps: iconProps,
        onClick: () => {
            logOnClickDatapoint(SMART_DOC_MENU_CLICKED, logData, { subtype: optionString });
            createMessageWithAttachmentAction(clientItem, null /* suggestion */, option);
        },
    };
}

function extractSuggestionInfoForLogging(suggestion: ReplyWithAttachmentSuggestion) {
    return {
        endpoint: ReplyWithEndpointType[suggestion.Endpoint],
        fileSourceType: suggestion.FileSourceType,
    };
}
